<template>
	<div class="multistep-form-wizard">
		<design2021-apprentice-multistep-form-wizard-splashscreen
			v-if="useSplashScreen && selectedSplashOption === null"
			:splash-options="splashOptions"
			@selected-splash-option="updateSelectedSplashOption">
		</design2021-apprentice-multistep-form-wizard-splashscreen>

		<form
			class="form"
			id="multistepWizardForm"
			v-else>
			<design-2021-apprentice-multistep-form-wizard-form-component
				v-for="(step, needle) in steps"
				v-show="activeIndexStep === step.step_number"
				:steps="steps"
				:current-step="activeIndexStep"
				:key="needle"
				:id="`step--${needle}`"
				:ref="`form-step-${needle}`"
				:index="needle">
				<template
					v-for="(_, name) in $slots"
					v-slot:[name]>
					<slot :name="name"></slot>
				</template>
			</design-2021-apprentice-multistep-form-wizard-form-component>

			<div class="steps-navigate">
				<div class="steps-pagination">
					<b-button
						class="btn-previous-step"
						variant="outline-primary"
						:disabled="parseInt(activeIndexStep) === 1 && !canExitForm"
						@click="handleClick"
                        dusk="previous-step-button">
                        {{activeIndexStep === 1 && canExitForm ? $t('common.cancel') : $t('common.previous')}}
					</b-button>
					<b-button
						class="btn-next-step"
						v-if="activeIndexStep !== steps.length"
						variant="primary"
						@click="nextStep"
                        dusk="next-step-button">
						{{$t('common.next')}}
					</b-button>
					<b-button
						class="btn-save btn-submit-form"
						v-else
						variant="primary"
						@click="submitForm"
                        dusk="submit-multistep-form-button">
						{{$t('common.save')}}
					</b-button>
				</div>
			</div>
		</form>
	</div>
</template>

<script>
	import Design2021ApprenticeMultistepFormWizardSplashscreen from './design-2021-apprentice-multistep-form-wizard-splashscreen.vue';

	export default {
		name: 'design-2021-apprentice-multistep-form-wizard-component',
		components: { Design2021ApprenticeMultistepFormWizardSplashscreen },
		emits: ['update-selected-splash-option', 'update-current-page', 'update-current-page', 'submit-form'],
		props: {
			useSplashScreen: {
				type: Boolean,
				required: false,
				default: false,
			},

			splashOptions: {
				type: Array,
				required: false,
				default: () => {
					return [];
				},
			},

			steps: {
				type: Array,
				required: true,
			},

			canExitForm: {
				type: Boolean,
				required: false,
				default: false,
			},
		},

		data() {
			return {
				selectedSplashOption: null,
				activeIndexStep: 1,
				activePageIndex: 1,
			};
		},

		destroyed() {
			window.removeEventListener('resize', this.handleResize);
		},

		mounted() {
			window.addEventListener('resize', this.handleResize);
			this.handleResize();
		},

		methods: {
			updateSelectedSplashOption(option) {
				this.selectedSplashOption = option.id;
				this.$emit('update-selected-splash-option', option.id);
			},

			prevStep() {
				const step = this.steps.filter((step) => step.step_number === this.activeIndexStep);
				if (step[0].child_steps) {
					if (this.activePageIndex === 1) {
						this.activeIndexStep--;
					} else {
						this.activePageIndex--;
						this.$emit('update-current-page', this.activeIndexStep);
					}
				} else {
					this.activeIndexStep--;
				}
			},

			nextStep() {
				const step = this.steps.filter((step) => step.step_number === this.activeIndexStep);
				if (this.$refs[this.needle][0].$children[1].checkForErrors) {
					const hasErrors = this.$refs[this.needle][0].$children[1].checkForErrors();
					hasErrors.then(
						(response) => {
							this.navigateNextStep(response, step);
						},
						(error) => {
							this.alert.errors();
						}
					);
				} else {
					this.navigateNextStep(false, step);
				}
			},

			handleClick() {
				if (this.canExitForm && this.activeIndexStep === 1) {
					window.location.href = this.routes.groupCreate();
				} else {
					this.prevStep();
				}
			},

			navigateNextStep(validate, step) {
				if (!validate) {
					if (step[0].child_steps) {
						if (this.activePageIndex === step[0].child_steps.length) {
							this.activeIndexStep++;
						} else {
							this.activeIndexStep++;
						}
					} else {
						this.activeIndexStep++;
					}

					this.$emit('update-current-page', this.activeIndexStep);
				}
			},

			submitForm() {
				this.$emit('submit-form');
			},

			handleResize() {
				const el = document.getElementById('multistep-wizard-form-steps');
				if (el) {
					const formEl = document.getElementById('multistepWizardForm');
					formEl.style.setProperty('--steps-width', `${el.clientWidth}px`);
				}
			},
		},

		computed: {
			needle() {
				return `form-step-${this.activeIndexStep - 1}`;
			},
		},
	};
</script>

<style scoped lang="scss">
	form ::v-deep {
		height: 100%;
		position: relative;

		.form-group {
			margin-bottom: $grid-gutter-width;
		}
	}

	.steps-navigate {
		margin-top: map-get($grid-gutter-spacing, 'forty-two');

		.btn:not(:last-child) {
			margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
		}
	}

	.btn-previous-step,
	.btn-next-step,
	.btn-submit-step,
	.btn-submit-form {
		font-size: $default-font-size;
		width: 100%;
	}

	@media (min-width: map-get($grid-breakpoints, 'md')) {
		form ::v-deep {
			&:after {
				content: '';
				position: absolute;
				display: block;
				top: 0;
				left: var(--steps-width);
				height: 100%;
				border-right: 4px dotted $border-color;
			}
		}

		.steps-navigate {
			margin-left: calc(var(--steps-width) + 30px);

			.btn:not(:last-child) {
				margin-bottom: 0;
			}
		}

		.steps-pagination {
			display: flex;
			align-items: center;
			justify-content: flex-start;
			column-gap: $grid-gutter-width;
		}

		.btn-previous-step,
		.btn-next-step,
		.btn-submit-step,
		.btn-submit-form {
			width: auto;
		}
	}

	@media (min-width: map-get($grid-breakpoints, 'xxl')) {
		.steps-navigate {
			margin-left: calc(var(--steps-width) + 60px);
		}
	}
</style>
